import styled from "styled-components";
import { CTA_HIDE_MS, PILL_WIDTH_ANIMATION_MS } from "./animationTiming";

const PILL_ANIMATION_DURATION = `${PILL_WIDTH_ANIMATION_MS}ms`;
const CTA_ANIMATION_DURATION = `${CTA_HIDE_MS}ms`;
const PILL_ANIMATION_EASING = "ease-in-out";

export const Container = styled.div(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    width: "100%",
    marginTop: "6px",
    backgroundColor: theme.colors[theme.colorMode].background_Primary,
    padding: `0px ${theme.spacing.medium}`,
}));

export const Content = styled.div({
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "46px",
    width: "100%",
});

/**
 * Pill shape, clipping, and width animation. Collapsed/expanded looks are painted by the
 * surface layers below (CSS cannot transition between two gradient backgrounds).
 * Fixed px widths while animating; `auto` when settled so content is never clipped.
 */
export const SavingsPill = styled.div<{
    isVisibility?: boolean;
    isExpanded?: boolean;
    isLayoutAnimating?: boolean;
    collapsedMaxWidthPx?: number;
    expandedMaxWidthPx?: number;
}>(({ theme, isVisibility, isExpanded, isLayoutAnimating, collapsedMaxWidthPx = 0, expandedMaxWidthPx = 0 }) => {
    const getWidth = () => {
        if (isExpanded) {
            return `${expandedMaxWidthPx}px`;
        }
        // px↔px interpolates; `auto`↔px does not.
        if (isLayoutAnimating) {
            return `calc(${collapsedMaxWidthPx}px + ${theme.spacing.xSmall} * 2)`;
        }
        return "auto";
    };

    return {
        position: "relative",
        display: "flex",
        justifyContent: "flex-start",
        marginRight: theme.spacing.xSmall,
        alignItems: "center",
        visibility: isVisibility ? "visible" : "hidden",
        flexShrink: 0,
        boxSizing: "border-box",
        padding: `${theme.spacing.xxSmall} ${theme.spacing.xSmall}`,
        borderRadius: theme.cornerRadius.medium,
        // Clip during expand/collapse so copy swapped at tween start is uncovered/covered by width.
        overflow: isLayoutAnimating || isExpanded ? "hidden" : "visible",
        whiteSpace: "nowrap",
        width: getWidth(),
        transition: isLayoutAnimating ? `width ${PILL_ANIMATION_DURATION} ${PILL_ANIMATION_EASING}` : "none",
    };
});

const surfaceLayerBase = {
    position: "absolute" as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: "inherit",
    boxSizing: "border-box" as const,
    pointerEvents: "none" as const,
    transition: `opacity ${PILL_ANIMATION_DURATION} ${PILL_ANIMATION_EASING}`,
};

/** Collapsed fill + solid border. Fades out when expanded. */
export const PillSolidSurface = styled.div<{ isExpanded?: boolean }>(({ theme, isExpanded }) => ({
    ...surfaceLayerBase,
    background: theme.colors[theme.colorMode].positive_Sub,
    border: `1px solid ${theme.colors[theme.colorMode].border_Tertiary}`,
    opacity: isExpanded ? 0 : 1,
}));

/**
 * Expanded fill. Solid border by default; engines with `mask-composite: exclude` get a
 * left-to-right fade-out ring instead.
 */
export const PillGradientSurface = styled.div<{ isExpanded?: boolean }>(({ theme, isExpanded }) => ({
    ...surfaceLayerBase,
    background: "linear-gradient(90deg, #C8F9E5 50%, rgba(200, 249, 229, 0.00) 100%)",
    opacity: isExpanded ? 1 : 0,
    border: `1px solid ${theme.colors[theme.colorMode].border_Tertiary}`,
    "@supports (mask-composite: exclude)": {
        border: "none",
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            borderRadius: "inherit",
            padding: "1px",
            background: `linear-gradient(90deg, ${theme.colors[theme.colorMode].border_Tertiary} 0%, transparent 100%)`,
            WebkitMask: "linear-gradient(#fff, #fff) content-box, linear-gradient(#fff, #fff)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            pointerEvents: "none",
        },
    },
}));

export const PillContent = styled.div({
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
});

/** Off-screen node used to measure natural text width. */
export const HiddenMeasure = styled.div({
    position: "absolute",
    left: -9999,
    top: 0,
    visibility: "hidden",
    pointerEvents: "none",
    whiteSpace: "nowrap",
});

/**
 * Pinned to the right edge while the pill animates. In flow the pill's tweening width would
 * push the CTA sideways, which is visible on narrow rows where there is no slack to absorb it.
 */
export const HeaderCta = styled.div<{
    isHidden?: boolean;
    isLoading?: boolean;
    isPinnedRight?: boolean;
}>(({ isHidden, isLoading, isPinnedRight }) => {
    // eslint-disable-next-line no-nested-ternary
    const opacity = isHidden ? 0 : isLoading ? 0.5 : 1;

    const positioning = isPinnedRight
        ? ({
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
          } as const)
        : ({ marginLeft: "auto" } as const);

    return {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0,
        whiteSpace: "nowrap",
        pointerEvents: isLoading || isHidden ? "none" : "auto",
        transition: `opacity ${CTA_ANIMATION_DURATION} ${PILL_ANIMATION_EASING}`,
        opacity,
        ...positioning,
    };
});

// Dashed horizontal separator shown between the quick-checkout header and footer.
export const Divider = styled.div(({ theme }) => ({
    width: "100%",
    borderTop: `1px dashed ${theme.colors[theme.colorMode].background_Button_Disabled}`,
}));

export const IconWrapper = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing.xxSmall,
    borderRadius: "50%",
    backgroundColor: theme.colors[theme.colorMode].background_Secondary,
    marginLeft: theme.spacing.xxSmall,
}));

const DELIVERY_COUNT_FADE_IN = "quickCheckoutDeliveryCountFadeIn";

/**
 * Fades out with the CTA while the pill is still collapsed, then leaves the flow for the width
 * tween (in flow it would overflow the row, since both it and the pill are `flexShrink: 0`).
 */
export const DeliveryCountTextWrapper = styled.div<{
    isFaded?: boolean;
    isRemoved?: boolean;
}>(({ isFaded, isRemoved }) => ({
    display: isRemoved ? "none" : "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    whiteSpace: "nowrap",
    opacity: isFaded ? 0 : 1,
    transition: `opacity ${CTA_ANIMATION_DURATION} ${PILL_ANIMATION_EASING}`,
    // Restarts when the node returns from `display: none` at settle, fading the text back in.
    animation: `${DELIVERY_COUNT_FADE_IN} ${CTA_ANIMATION_DURATION} ${PILL_ANIMATION_EASING}`,
    [`@keyframes ${DELIVERY_COUNT_FADE_IN}`]: {
        from: { opacity: 0 },
        to: { opacity: 1 },
    },
}));
